/**
 * Registrar V2 App.tsx's column composition over IMAM's existing table engine.
 * Donor table/control rendering lives in shared RecordTable, not a second grid.
 * The server owns order, filtering and pages; Selected owns hidden exact IDs.
 */
import { useContext, useState, type Dispatch, type SetStateAction } from "react";

import { RecordTable } from "@/components/records/RecordTable";
import { facetColumnId, type RecordTableEngine } from "@/components/records/record-table-engine";
import { DataTableFacetedFilter } from "@/components/records/donor/data-table-faceted-filter";
import { DataTableViewOptions } from "@/components/records/donor/data-table-view-options";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { BentoVisit } from "@/components/ui/box-visit-context";
import type { BoxState, ListBox, RowAct } from "@/components/ui/box.schema";
import { Button } from "@/components/ui/button";
import type { PeopleRegisterResponse, PeopleRegisterRow } from "@/lib/api/people";
import { personName } from "@/lib/person-name";

import { RELATIONSHIP_LABEL } from "../person-record";
import { accessWord, selectedOf, type Selected } from "./register-boxes";
import { registerCopy as copy } from "./register-copy";

export function PeopleRegisterTable({
  response, selection, setSelection, onPage, onClearFilters, stateOf, actOf, emptySentence, find,
}: {
  response: PeopleRegisterResponse;
  find?: ListBox<PeopleRegisterResponse, PeopleRegisterRow>["find"];
  selection: Map<string, Selected>;
  setSelection: Dispatch<SetStateAction<Map<string, Selected>>>;
  onPage: (page: number) => void;
  onClearFilters: () => void;
  stateOf: (row: PeopleRegisterRow) => BoxState | null;
  actOf: (row: PeopleRegisterRow) => RowAct | undefined;
  emptySentence: string;
}) {
  const host = useContext(BentoVisit);
  const [hiddenColumns, setHiddenColumns] = useState(["email", "program"]);
  const [typed, setTyped] = useState(find?.query ?? "");
  const [heldQuery, setHeldQuery] = useState(find?.query);
  if (find?.query !== heldQuery) {
    setHeldQuery(find?.query);
    if (find?.query !== undefined) setTyped(find.query);
  }
  const facets = find?.facets ?? [];
  const toolbar = (engine: RecordTableEngine<PeopleRegisterRow>) => {
    const filter = (label: string) => {
      const facet = facets.find(value => value.label === label)!;
      const bucket = engine.facetBuckets.find(value => value.facet.id === label)!;
      return <fieldset key={label} disabled={facet.read?.status === "loading"} className="min-w-0">
        <DataTableFacetedFilter title={label} single column={engine.table.getColumn(facetColumnId(label))!} options={bucket.options.map(option => ({ ...option, label: option.label ?? option.value }))} />
        {facet.read?.status === "failed" && <p className="text-meta">{facet.read.sentence} <Button type="button" variant="ghost" size="sm" onClick={facet.read.retry}>Try again</Button></p>}
      </fieldset>;
    };
    const scope = facets.find(value => value.label === copy.view);
    const primary = facets.filter(value => (value.label === copy.relationship || value.label === copy.access));
    const more = facets.filter(value => value !== scope && !primary.includes(value));
    const activeMore = more.filter(value => value.value !== value.options[0]?.value).length;
    return <div className="grid gap-2 pb-3">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        {find && <Input type="search" className="min-w-48 flex-1 sm:max-w-xs" aria-label={find.placeholder} placeholder={find.placeholder} value={typed} onChange={event => { setTyped(event.target.value); find.onQueryChange?.(event.target.value); }} />}
        {scope && <NativeSelect aria-label="People scope" className="w-full sm:w-auto" value={scope.value} onChange={event => scope.onChange(event.target.value)}>{scope.options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</NativeSelect>}
        {primary.map(value => filter(value.label))}
        {more.length > 0 && <Popover><PopoverTrigger asChild><Button type="button" variant="ghost" size="sm">More filters{activeMore ? ` · ${activeMore}` : ""}</Button></PopoverTrigger><PopoverContent align="start" className="grid w-72 gap-2 p-4">{more.map(value => filter(value.label))}</PopoverContent></Popover>}
        <div className="ms-auto hidden md:block"><DataTableViewOptions table={engine.table} options={engine.hideableColumns.map(column => ({ id: column.id, label: column.name, group: "columns" as const }))} /></div>
      </div>
      <p className="text-meta text-muted-foreground" aria-live="polite">{response.total} {response.total === 1 ? "person" : "people"} · Name order</p>
    </div>;
  };
  const open = (id: string) => host?.visit(`person:${id}`, { box: "register" });
  const invitation = (row: PeopleRegisterRow) => {
    const state = stateOf(row);
    const nativeAct = actOf(row);
    // Register facts can name access work, but only the exact resolver reads
    // whether an existing account or an invitation is the authorized path.
    const canReview = !row.user_id && row.role === null && !row.current_invitation
      && !row.archived_at;
    const exactAccess = nativeAct?.does === "visit" && nativeAct.visit.endsWith("/access");
    const act = exactAccess ? nativeAct : canReview ? { does: "visit" as const, verb: state?.class === "draft" ? state.word : "Set up sign-in", visit: `person:${row.person_id}/access` as const } : nativeAct;
    if (act?.does === "visit" && act.visit.endsWith("/access") && host) {
      return <Button type="button" variant="ghost" size="sm" data-act="visit" data-visit-to={act.visit}
        onClick={() => host.visit(act.visit, { box: "register" })}>{act.verb}</Button>;
    }
    return state ? <span>{state.word}</span> : null;
  };

  return <RecordTable
    rows={response.rows}
    rowId={row => row.person_id}
    primary={{ header: "Person", label: row => <span className="inline-flex min-w-0 flex-wrap items-baseline gap-2"><span dir="auto">{personName(row.display_name)}</span>{row.archived_at && <span className="text-meta text-muted-foreground">Archived</span>}</span>, ariaLabel: row => personName(row.display_name), detail: row => [row.email ?? "Email not recorded", row.sid].filter(Boolean).join(" · ") }}
    columns={[
      { id: "relationship", header: copy.relationship, cell: row => row.relationships.map(value => RELATIONSHIP_LABEL[value]).join(", ") },
      { id: "email", header: "Email", cell: row => row.email ? <span dir="auto" className="break-all">{row.email}</span> : "Not recorded" },
      { id: "program", header: copy.program, cell: row => row.program_name },
      { id: "access", header: copy.access, cell: accessWord },
    ]}
    columnVisibility
    hiddenColumns={hiddenColumns}
    onHiddenColumnsChange={setHiddenColumns}
    toolbar={toolbar}
    facets={facets.map(facet => ({ id: facet.label, label: facet.label, value: () => null, options: facet.options, single: true }))}
    facetSelection={Object.fromEntries(facets.map(facet => [facet.label, facet.value ? [facet.value] : []]))}
    onFacetSelectionChange={next => {
      for (const facet of facets) {
        const value = next[facet.label]?.[0] ?? facet.options[0]?.value ?? "";
        if (value !== facet.value) facet.onChange(value);
      }
    }}
    selectable
    selectionSummary={false}
    selectedRowIds={[...selection.keys()]}
    onSelectedRowIdsChange={ids => setSelection(current => {
      const next = new Map(current);
      const selected = new Set(ids);
      for (const id of next.keys()) if (!selected.has(id)) next.delete(id);
      for (const row of response.rows) if (selected.has(row.person_id)) next.set(row.person_id, selectedOf(row));
      return next;
    })}
    manual={{ sort: true, facets: true, pagination: true }}
    pageSize={response.page_size}
    pageIndex={Math.max(0, response.page - 1)}
    rowCount={response.total}
    onPageIndexChange={index => onPage(index + 1)}
    onOpen={host ? open : undefined}
    rowAction={invitation}
    card={row => <div className="grid min-w-0 gap-1">
      <p dir="auto" className="font-medium">{personName(row.display_name)}</p>
      <p className="text-meta text-muted-foreground">{[row.sid, row.relationships.map(value => RELATIONSHIP_LABEL[value]).join(", "), row.program_name].filter(Boolean).join(" · ")}</p>
      {row.email && <p dir="auto" className="break-all text-meta">{row.email}</p>}
      <p className="text-meta">{row.archived_at ? "Archived · " : ""}{accessWord(row)}</p>
    </div>}
    emptyState={<div className="grid justify-items-start gap-3 py-8">
      <p>{emptySentence}</p>
      <Button type="button" variant="outline" onClick={onClearFilters}>Clear search and filters</Button>
    </div>}
  />;
}
